import type { LucideIcon } from "lucide-react";

import { cn } from "@/shared/lib/utils";

interface StatCardProps {
	/** Small-caps label (the "key"). */
	label: string;
	/** The headline value, rendered in the Geist display face. */
	value: string;
	/** Optional supporting line under the value. */
	hint?: string;
	/** Optional lucide icon shown in a tinted chip in the corner. */
	icon?: LucideIcon;
	className?: string;
}

/**
 * A single designed stat tile: a small-caps label over a display value, with an
 * optional icon chip and supporting hint. Built on the brand card surface — flat
 * `--card` cream fill, a warm hairline border, the 20px brand radius, and NO shadow
 * (the resources-hub treatment). Hover lift is owned by the global motion layer.
 * The value uses the Geist display face (the brand has no serif).
 */
export function StatCard({ label, value, hint, icon: Icon, className }: StatCardProps) {
	return (
		<div
			className={cn(
				"group/stat relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card p-5",
				className,
			)}
		>
			<div className="flex items-start justify-between gap-3">
				<span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
				{Icon && (
					<span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border bg-background text-primary">
						<Icon className="h-[18px] w-[18px]" aria-hidden="true" />
					</span>
				)}
			</div>
			<div className="mt-3 text-xl font-semibold leading-tight tracking-tight text-foreground">{value}</div>
			{hint && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{hint}</p>}
		</div>
	);
}
